// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type Stock } from '@shared/data-access/interfaces/api/ai/screener';
import { AutocompleteInput } from '@shared/ui-kit/components/autocomplete';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { useForm, useValidateTicker } from '@advisor-app/hooks';
import { assetsApi } from '@shared/data-access/api/ai/assets-api';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@advisor-app/store/store';
import {
  selectUniverseFilters,
  updateUniverseFilters,
} from '@advisor-app/store/slices/parameters';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';

interface TickersProps {
  type: 'Include' | 'Exclude';
  id: string;
}

export const Tickers = ({ type, id }: TickersProps) => {
  const dispatch = useDispatch();
  const { to_exclude, to_include } = useAppSelector(state =>
    selectUniverseFilters(state, id),
  )!;

  const tickers = useMemo(
    () => (type === 'Include' ? to_include : to_exclude),
    [type, to_exclude, to_include],
  );

  const {
    values: { ticker },
    handleInputChange,
    handleResetForm,
  } = useForm({
    ticker: '',
  });

  const addRow = (e: React.SyntheticEvent) => {
    e.preventDefault();

    const updatedTickers = [...tickers, ticker];
    const updatePayload =
      type === 'Include'
        ? { to_include: updatedTickers }
        : { to_exclude: updatedTickers };

    dispatch(updateUniverseFilters([id, updatePayload]));
    handleResetForm();
  };

  const { data: allTickers = [] as unknown as Stock[] } =
    assetsApi.useAssetsInfoQuery(
      {
        params: { detail: true },
        payload: tickers,
        assetType: 'stock',
      },
      { skip: tickers.length === 0 },
    );

  const handleDeleteTicker = (ticker: string) => {
    const updatedTickers = tickers.filter(tickerItem => tickerItem !== ticker);
    const updatePayload =
      type === 'Include'
        ? { to_include: updatedTickers }
        : { to_exclude: updatedTickers };

    dispatch(updateUniverseFilters([id, updatePayload]));
  };

  const { isValidTicker } = useValidateTicker(ticker, 'stock');

  return (
    <div className="relative flex flex-col items-start space-y-4">
      <form
        className="relative grid grid-flow-row items-start gap-y-4"
        autoComplete="off"
        onSubmit={addRow}
      >
        <label className="font-semibold text-title-dark">
          <Translation
            i18nKey={`research.${type.toLowerCase()}` as any}
            ns="research"
          />
        </label>
        <fieldset className="grid grid-flow-col items-center justify-start gap-x-4">
          <AutocompleteInput
            onChangeValue={e => handleInputChange(e, { toUpperCase: true })}
            onSelectValue={e => handleInputChange(e, { toUpperCase: true })}
            value={ticker}
            name="ticker"
            error={isValidTicker || !ticker}
            noMargin
            className="!min-w-[209px] max-w-[209px]"
            assetType="stocks"
          />

          <IconButton
            type="submit"
            icon="Add"
            variant="icon"
            color="disabled"
            hoverable={false}
            className="hover:!text-primary"
            disabled={!isValidTicker}
            data-testid="submit-ticker"
          />
        </fieldset>
        {!isValidTicker && !!ticker && (
          <span className="text-sm text-red-500" data-testid="alert-invalid">
            Ticker {ticker} is invalid
          </span>
        )}
      </form>

      <div className="flex flex-col space-y-4 empty:hidden">
        {tickers.map((ticker, i) => (
          <div
            className="flex flex-1 flex-row items-center rounded-lg bg-primary/5 px-3 py-2 transition-colors hover:cursor-pointer hover:bg-primary/25"
            key={`${i}-${ticker}`}
            onClick={() => handleDeleteTicker(ticker)}
          >
            <div className="flex min-w-[209px] flex-row items-center space-x-2">
              <div className="flex items-center justify-center">
                {allTickers ? (
                  <img
                    src={
                      (allTickers as Stock[]).find(value => value.ticker === ticker)
                        ?.url_logo
                    }
                    alt={`Logo of ${ticker} stock`}
                    className="h-[25px] w-[25px] rounded-full object-contain object-center"
                  />
                ) : null}
              </div>
              <span className="font-[poppins] font-normal leading-6 text-border-dark">
                {ticker}
              </span>
            </div>
            <IconButton
              variant="icon"
              icon="Eliminate"
              size="lg"
              color="danger"
              onClick={() => handleDeleteTicker(ticker)}
              data-testid="delete-ticker"
            />
          </div>
        ))}
      </div>
    </div>
  );
};
